import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import { Search, Check, X, ChevronLeft, ChevronRight, MoveVertical } from "lucide-react";
import {
  useSearchUnsplash,
  UnsplashPhoto,
  useSetTripCoverPhoto,
  useRemoveTripCoverPhoto,
  useUpdateCoverPhotoPosition,
  getCoverPhotoUrl,
} from "@/hooks/useUnsplash";
import { useDebounce } from "@/hooks/useDebounce";
import { cn } from "@/lib/utils";

interface PhotoPickerDialogProps {
  tripId: string;
  destination?: string | null;
  currentPhotoId?: string | null;
  currentPositionY?: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PhotoPickerDialog({
  tripId,
  destination,
  currentPhotoId,
  currentPositionY,
  open,
  onOpenChange,
}: PhotoPickerDialogProps) {
  const [searchQuery, setSearchQuery] = useState(destination || "");
  const [page, setPage] = useState(1);
  const [selectedPhoto, setSelectedPhoto] = useState<UnsplashPhoto | null>(null);
  const [positionY, setPositionY] = useState(currentPositionY ?? 50);
  const [showPositionAdjust, setShowPositionAdjust] = useState(false);

  const debouncedQuery = useDebounce(searchQuery, 300);

  const { data, isLoading, isFetching } = useSearchUnsplash(debouncedQuery, page, open);
  const setTripCoverPhoto = useSetTripCoverPhoto();
  const removeTripCoverPhoto = useRemoveTripCoverPhoto();
  const updateCoverPhotoPosition = useUpdateCoverPhotoPosition();

  // Initialize search with destination when dialog opens
  useEffect(() => {
    if (open && destination) {
      setSearchQuery(destination);
      setPage(1);
      setSelectedPhoto(null);
      setPositionY(currentPositionY ?? 50);
      setShowPositionAdjust(false);
    }
  }, [open, destination, currentPositionY]);

  const handleConfirm = async () => {
    if (!selectedPhoto) return;

    await setTripCoverPhoto.mutateAsync({
      tripId,
      photo: selectedPhoto,
    });

    // If position was adjusted, also update position
    if (positionY !== 50) {
      await updateCoverPhotoPosition.mutateAsync({
        tripId,
        positionY,
      });
    }

    onOpenChange(false);
    setSelectedPhoto(null);
  };

  const handleRemove = async () => {
    await removeTripCoverPhoto.mutateAsync(tripId);
    onOpenChange(false);
    setSelectedPhoto(null);
  };

  const handleSavePosition = async () => {
    await updateCoverPhotoPosition.mutateAsync({
      tripId,
      positionY,
    });
    setShowPositionAdjust(false);
  };

  const photos = data?.photos || [];
  const totalPages = data?.totalPages || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Choose Cover Photo</DialogTitle>
          <DialogDescription>
            Search for and select a cover photo for your trip from Unsplash.
          </DialogDescription>
        </DialogHeader>

        {/* Position Adjustment Mode */}
        {showPositionAdjust && currentPhotoId ? (
          <div className="space-y-4">
            {/* Preview with position adjustment */}
            <div className="relative aspect-[3/1] rounded-lg overflow-hidden bg-muted">
              <img
                src={getCoverPhotoUrl(
                  photos.find(p => p.id === currentPhotoId)?.url ||
                  (data?.photos?.find(p => p.id === currentPhotoId)?.url) ||
                  "",
                  'hero'
                ) || ""}
                alt="Cover photo preview"
                className="w-full h-full object-cover"
                style={{ objectPosition: `center ${positionY}%` }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/40 pointer-events-none" />
            </div>

            {/* Position Slider */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MoveVertical className="h-4 w-4" />
                <span>Adjust vertical position</span>
              </div>
              <Slider
                value={[positionY]}
                onValueChange={([value]) => setPositionY(value)}
                min={0}
                max={100}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Top</span>
                <span>Center</span>
                <span>Bottom</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowPositionAdjust(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSavePosition}
                disabled={updateCoverPhotoPosition.isPending}
              >
                {updateCoverPhotoPosition.isPending ? "Saving..." : "Save Position"}
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search for photos..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="pl-10"
              />
            </div>

            {/* Photo Grid */}
        <ScrollArea className="flex-1 min-h-[300px] pr-4">
          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pb-16">
              {Array.from({ length: 9 }).map((_, i) => (
                <Skeleton key={i} className="aspect-[3/2] rounded-lg" />
              ))}
            </div>
          ) : photos.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-muted-foreground">
              {searchQuery ? "No photos found. Try a different search." : "Enter a search term to find photos."}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pb-16">
              {photos.map((photo) => {
                const thumbUrl = getCoverPhotoUrl(photo.url, 'thumbnail');
                return (
                  <button
                    key={photo.id}
                    onClick={() => setSelectedPhoto(photo)}
                    className={cn(
                      "relative aspect-[3/2] rounded-lg overflow-hidden border-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary",
                      selectedPhoto?.id === photo.id
                        ? "border-primary ring-2 ring-primary"
                        : photo.id === currentPhotoId
                          ? "border-green-500"
                          : "border-transparent hover:border-muted-foreground/50"
                    )}
                  >
                    <img
                      src={thumbUrl || photo.url}
                      alt={photo.altDescription || "Photo option"}
                      className="w-full h-full object-cover"
                    />
                    {selectedPhoto?.id === photo.id && (
                      <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                        <Check className="h-8 w-8 text-white drop-shadow-lg" />
                      </div>
                    )}
                    {photo.id === currentPhotoId && selectedPhoto?.id !== photo.id && (
                      <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded">
                        Current
                      </div>
                    )}
                    {/* Mini attribution */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                      <p className="text-[10px] text-white/80 truncate">
                        {photo.photographerName}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 py-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || isFetching}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || isFetching}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}

            {/* Actions */}
            <div className="flex justify-between pt-4 border-t">
              <div className="flex gap-2">
                {currentPhotoId && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRemove}
                      disabled={removeTripCoverPhoto.isPending}
                      className="text-destructive hover:text-destructive"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Remove Photo
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPositionAdjust(true)}
                    >
                      <MoveVertical className="h-4 w-4 mr-2" />
                      Adjust Position
                    </Button>
                  </>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirm}
                  disabled={!selectedPhoto || setTripCoverPhoto.isPending}
                >
                  {setTripCoverPhoto.isPending ? "Saving..." : "Use This Photo"}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
